import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button, Col, Collapse, Row, Badge } from "reactstrap";
import { Whisper, Popover } from "rsuite";

import DropdownOptionWithIcon from "../../components/Menus/DropdownOptionWithIcon";
import DropdownMenuDivider from "../../components/Menus/DropdownMenuDivider";
import PopoverDropdownMenu from "../../components/Menus/PopoverDropdownMenu";
import { deleteFileBlobStorage } from "../../utils/FileUtils";
import { FileInterface } from "../../types/RecordInterfaces";
import { pathnameFromURL } from "../../utils/GeneralUtils";
import { getFileURL } from "../../utils/GeneralUtils";
import AppSpinner from "../../elements/AppSpinner";
import Constants from "../../utils/Constants";
import DisplayRecord from "./DisplayRecord";
import UppyFile from "../Inputs/UppyFile";
import Preview from "../../utils/Preview";
import EmptyField from "./EmptyField";

interface Props {
  field: any;
  existingValue?: FileInterface;
  recordID: string;
  submitting: boolean;
  error: any;
  table?: boolean;
  onUpdate: (data: any) => void;
  type: "Image" | "Video" | "Audio" | "Text" | "Data" | "SingleFile" | null;
  mode: "r" | "rw";
  moduleAPIName: string;
  subitemID?: string;
}

const MediaRecord = (
  {
    field,
    existingValue,
    recordID,
    submitting,
    table = false,
    onUpdate,
    type,
    mode,
    moduleAPIName,
    subitemID,
  }: Props,
  ref: React.Ref<any>
) => {
  const [keepOpen, setKeepOpen] = useState(false);
  const [edit, setIsEditing] = useState(false);
  const [mediaSubmit, setMediaSubmit] = useState(false);
  const [addOpen, setAddOpen] = useState(!existingValue);
  const [vizualizerValue, setVizualizerValue] = useState<{
    File?: string;
    URL?: string;
    PublicURL?: string;
    source?: string;
    [key: string]: any;
  } | null>(null);
  const whisperOptionsRef = useRef<any>();

  const whisperRef = useRef<any>();

  let plugins = [
    "OneDrive",
    "GoogleDrive",
    "Box",
    "Facebook",
    "Instagram",
    "Webcam",
  ];

  if (type === Constants.FIELDTYPES.IMAGE) {
    plugins = [
      "OneDrive",
      "GoogleDrive",
      "Box",
      "Facebook",
      "Instagram",
      "Webcam",
    ];
  } else if (type === Constants.FIELDTYPES.VIDEO) {
    plugins = [
      "OneDrive",
      "GoogleDrive",
      "Box",
      "Facebook",
      "Instagram",
      "Webcam",
      "ScreenCapture",
    ];
  } else if (type === Constants.FIELDTYPES.AUDIO) {
    plugins = [
      "OneDrive",
      "GoogleDrive",
      "Box",
      "Facebook",
      "Instagram",
      "Audio",
    ];
  }

  const openModify = () => {
    setIsEditing(true);
    whisperRef.current?.open();
  };

  const save = (newValue: any | null = existingValue) => {
    if (JSON.stringify(newValue) === JSON.stringify(existingValue)) {
      return;
    }

    onUpdate({
      field,
      recordID,
      payload: {
        [field.APIName]: newValue,
      },
      onSuccess: () => {
        setMediaSubmit(false);
        setVizualizerValue(null);
      },
    });
  };

  const deleteRecord = async () => {
    try {
      if (!existingValue?.File) {
        setMediaSubmit(false);
        return;
      }
      const fieldAPIName = field.SubitemParent
        ? `${field.SubitemParent}.${field.APIName}`
        : field.APIName;

      deleteFileBlobStorage({
        filePath: existingValue.File,
        recordID,
        moduleAPIName,
        fieldAPIName,
        subitemID,
        onSuccess: () => {
          setMediaSubmit(true);
          save(null);
        },
      });
    } catch (e) {
      setMediaSubmit(false);
      console.error(e);
    }
  };

  const options = [
    {
      name: "Open In New Tab",
      icon: "open-in-new",
      action: () => {
        whisperRef.current.open();
        whisperOptionsRef?.current?.close();
        window.open(
          vizualizerValue?.PublicURL
            ? vizualizerValue?.PublicURL
            : vizualizerValue?.source,
          "_blank"
        );
      },
    },
    {
      name: `Remove ${type}`,
      icon: "delete",
      action: () => deleteRecord(),
    },
  ];

  useEffect(() => {
    if (!existingValue) return setVizualizerValue(null);

    if (existingValue?.PublicURL) {
      setVizualizerValue({
        ...existingValue,
        source: existingValue?.PublicURL,
      });
      return;
    }

    getFileURL(
      existingValue?.File
        ? existingValue?.File
        : existingValue?.URL
        ? pathnameFromURL(existingValue?.URL)
        : ""
    ).then((source) => {
      setVizualizerValue({ ...existingValue, source: source });
    });
  }, [existingValue, type]);

  const editingPopup = (
    <div>
      <div className="p-2 border-bottom border-superlight">
        <Row className="align-items-center">
          <Col xs="" className="pr-2 d-flex">
            <h5 className="m-0 pr-2">{field?.Name ? field?.Name : type}</h5>
            <Badge color="light">{field?.Public ? "Public" : "Private"}</Badge>
          </Col>
          <Col xs="auto">
            <Button
              size="sm"
              outline
              color="secondary"
              onClick={(e) => {
                e.preventDefault();
                setIsEditing(false);
                whisperRef?.current?.close();
              }}
            >
              Done
            </Button>
          </Col>
        </Row>
      </div>
      <div style={{ maxHeight: "350px" }} className="p-0">
        {existingValue?.File ? (
          <>
            <div style={{ position: "relative" }} className="h-100">
              <div className="p-4 h-100 w-100 tw:justify-center tw:items-center">
                {vizualizerValue ? (
                  <Preview
                    mode="preview "
                    value={vizualizerValue}
                    type={type || ""}
                    styles={{
                      maxWidth: "150px",
                      margin: "0 auto",
                    }}
                  />
                ) : (
                  "loading"
                )}
              </div>
              <div
                style={{ position: "absolute", right: "0.5rem", top: "0.5rem" }}
              >
                <Whisper
                  placement="autoVerticalEnd"
                  trigger={"click"}
                  ref={whisperOptionsRef}
                  speaker={
                    <Popover className="sidebar-popover" full>
                      <PopoverDropdownMenu>
                        <h5 className="px-2 pt-2 text-dark">
                          {`${type} Options`}
                        </h5>
                        <DropdownMenuDivider />
                        {options?.map((option, i) => {
                          return (
                            <DropdownOptionWithIcon
                              key={`${option.name}-${i}`}
                              name={option.name}
                              icon={`mdi mdi-${option.icon}`}
                              onClick={option.action}
                            />
                          );
                        })}
                      </PopoverDropdownMenu>
                    </Popover>
                  }
                  controlId="newRecordDropdown"
                >
                  <Button
                    size="sm"
                    outline
                    className="btn-icon-only"
                    style={{
                      height: "1.5rem",
                      width: "1.5rem",
                    }}
                  >
                    <i
                      className={`mdi mdi-dots-horizontal`}
                      style={{
                        fontSize: 20,
                        lineHeight: 1,
                        position: "relative",
                        top: 1,
                      }}
                    ></i>
                  </Button>
                </Whisper>
              </div>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
      <div
        onClick={() => [setAddOpen(!addOpen)]}
        className={`cursor-pointer p-2 bg-ultralight--hover text-dark border-top border-superlight ${
          addOpen && "bg-ultralight"
        }`}
      >
        <Row className="align-items-center">
          <Col xs="">
            {existingValue?.URL ? (
              <h5 className="text-dark m-0">Replace {type}</h5>
            ) : (
              <h5 className="text-dark m-0">Add {type}</h5>
            )}
          </Col>
          <Col xs="auto" className="pl-0">
            <Button
              size="sm"
              outline
              className="btn-icon-only"
              style={{
                height: "1.5rem",
                width: "1.5rem",
              }}
            >
              <i
                className={`mdi mdi-chevron-${addOpen ? "down" : "right"}`}
                style={{
                  fontSize: 20,
                  lineHeight: 1,
                  position: "relative",
                  top: 1,
                }}
              ></i>
            </Button>
          </Col>
        </Row>
      </div>
      <Collapse isOpen={addOpen}>
        <div className={`p-2 border-top border-superlight`}>
          <Row className="align-items-center">
            <Col xs="" className="tw:px-3">
              <UppyFile
                afterSubmit={(newFile) => {
                  onUpdate({
                    field,
                    recordID,
                    payload: {
                      [field.APIName]: newFile,
                    },
                  });
                }}
                fileType={type}
                plugins={plugins}
                meta={{ isPublic: !!field?.Public }}
                setKeepOpen={setKeepOpen}
                filesToFilter={existingValue ? [existingValue] : []}
              />
            </Col>
          </Row>
        </div>
      </Collapse>
    </div>
  );

  useImperativeHandle(ref, () => ({
    focus: () => {
      whisperRef.current?.open();
    },
  }));

  //Display File
  return (
    <>
      <Whisper
        preventOverflow
        ref={whisperRef}
        placement="auto"
        trigger={edit && !keepOpen ? "click" : "none"}
        speaker={
          <Popover
            className="sidebar-popover p-0 m-0"
            style={{ width: "350px" }}
          >
            {editingPopup}
          </Popover>
        }
      >
        <div>
          <DisplayRecord
            mode={mode}
            openModify={openModify}
            isSensitive={field?.Sensitive ?? false}
            tableView={table}
            displayComponent={() => (
              <div
                className="d-flex justify-content-between align-items-center text-center cursor-pointer w-100"
                onClick={() => {
                  if (mode === "rw") openModify();
                }}
              >
                {submitting || mediaSubmit ? (
                  <div className="px-2">
                    <AppSpinner size="sm" />
                  </div>
                ) : (
                  <>
                    {vizualizerValue ? (
                      <Preview
                        mode={table ? "table" : ""}
                        value={vizualizerValue}
                        type={type || ""}
                        hoverPreview={table}
                        styles={{ maxWidth: "100px" }}
                      />
                    ) : (
                      <EmptyField table={table} />
                    )}
                  </>
                )}
              </div>
            )}
            modifyTrigger={"click"}
          />
        </div>
      </Whisper>
    </>
  );
};

export default forwardRef(MediaRecord);
